/**
 * Match By Email Actor
 *
 * Calls the match-by-email endpoint to determine whether the patient
 * entering their email is already in the system.
 */

import { fromPromise } from "xstate";
import { z } from "zod";
import { trackNetworkRequest } from "../../lib/network-diagnostics";
import { matchByEmail } from "../../server-actions/consumer-actions";
import { fetchUserEmail } from "../../server-actions/user-actions";
import type { ActorMetadata, ActorWithMetadata } from "./send-sms.actor";

/**
 * Input Schema — what this actor needs from machine context.
 */
const MatchByEmailInputSchema = z.object({
  email: z.string(),
  orgId: z.number(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  dob: z.string().optional(),
  phoneNumber: z.string().optional(),
});

type MatchByEmailInput = z.infer<typeof MatchByEmailInputSchema>;

/**
 * Success Schema — what this actor returns on success.
 */
const MatchByEmailSuccessSchema = z.object({
  message: z.enum([
    "PATIENT_MATCHED",
    "NO_PATIENTS_MATCHED",
    "MULTIPLE_PATIENTS_MATCHED",
  ]),
  consumer: z
    .object({
      id: z.string(),
      userId: z.string(),
      firstname: z.string(),
      lastname: z.string(),
      dob: z.string(),
      sex: z.string().nullable(),
      streetAddress: z.string().nullable(),
      city: z.string().nullable(),
      state: z.string().nullable(),
      zip: z.string().nullable(),
      addressLine2: z.string().nullable(),
      ehrPatientId: z.string().nullable(),
      ehrEnterpriseId: z.string().nullable(),
      email: z.string().nullable(),
      insurances: z.array(z.any()).optional(),
      hipaaSignDate: z.string().nullable(),
      emergencyContactFirstName: z.string().nullable().optional(),
      emergencyContactLastName: z.string().nullable().optional(),
      emergencyContactPhone: z.string().nullable().optional(),
      emergencyContactRelationship: z.string().nullable().optional(),
      pcpFirstName: z.string().nullable().optional(),
      pcpLastName: z.string().nullable().optional(),
      pcpPhone: z.string().nullable().optional(),
      pcpAddress: z
        .object({
          streetAddress: z
            .union([
              z.string(),
              z.object({
                streetAddress: z.string().optional(),
                city: z.string().optional(),
                state: z.string().optional(),
                zip: z.string().optional(),
              }),
            ])
            .optional(),
          city: z.string().optional(),
          state: z.string().optional(),
          zip: z.string().optional(),
        })
        .nullable()
        .optional()
        .transform(
          (
            val,
          ):
            | {
                streetAddress?: string;
                city?: string;
                state?: string;
                zip?: string;
              }
            | null
            | undefined => {
            if (!val) return val;
            if (val.streetAddress && typeof val.streetAddress === "object") {
              const inner = val.streetAddress;
              const flat = {
                streetAddress: inner.streetAddress,
                city: inner.city,
                state: inner.state,
                zip: inner.zip,
              };
              return Object.values(flat).some((v) => v !== undefined)
                ? flat
                : null;
            }
            const flat = {
              streetAddress: val.streetAddress as string | undefined,
              city: val.city,
              state: val.state,
              zip: val.zip,
            };
            return Object.values(flat).some((v) => v !== undefined)
              ? flat
              : null;
          },
        ),
      pharmacyName: z.string().nullable().optional(),
      pharmacyPhone: z.string().nullable().optional(),
      pharmacyAddress: z
        .object({
          streetAddress: z.string().optional(),
          city: z.string().optional(),
          state: z.string().optional(),
          zip: z.string().optional(),
        })
        .nullable()
        .optional(),
      referralSource: z.string().nullable().optional(),
    })
    .nullable(),
  // Surfaced for direct use by sendSMSActor in the PATIENT_MATCHED path
  userId: z.string().optional(),
  consumerId: z.string().optional(),
  // Surfaced for the MULTIPLE_PATIENTS_MATCHED disambiguation path
  correlationId: z.string().optional(),
  disambiguationField: z.string().optional(),
});

type MatchByEmailSuccess = z.infer<typeof MatchByEmailSuccessSchema>;

/**
 * Actor Implementation
 */
const matchByEmailActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: MatchByEmailInput;
  }): Promise<MatchByEmailSuccess> => {
    const validated = MatchByEmailInputSchema.safeParse(input);

    if (!validated.success) {
      const error = new Error(
        `matchByEmail validation failed: ${validated.error.message}`,
      );
      throw error; // XState onError
    }

    const { email, orgId, firstName, lastName, dob, phoneNumber } =
      validated.data;

    // Strip the +1 country code prefix added by ContactInput's E.164 transform.
    // The match/by-email endpoint expects a 10-digit US number.
    const normalizedPhone = phoneNumber?.replace(/^\+1/, "");

    const tracker = trackNetworkRequest("matchByEmail", { orgId });
    try {
      const response = await matchByEmail({
        email,
        orgId,
        firstName,
        lastName,
        dob,
        phoneNumber: normalizedPhone,
      });
      tracker.success();

      const consumer = response.data.consumer;

      // Fetch email from User record (not on Consumer).
      // On fetch failure, fall back to the email the patient typed — the
      // match succeeded on it, so it's a valid email for this user.
      const emailResult = consumer?.userId
        ? await fetchUserEmail(consumer.userId)
        : null;
      const resolvedEmail = emailResult
        ? emailResult.status === "ok"
          ? emailResult.email
          : email
        : null;
      const consumerWithEmail = consumer
        ? { ...consumer, ...(resolvedEmail ? { email: resolvedEmail } : {}) }
        : consumer;
      return {
        message: response.data.message,
        consumer: consumerWithEmail,
        // Surface IDs for direct use by sendSMSActor in the PATIENT_MATCHED path
        userId: consumer?.userId ?? undefined,
        consumerId: consumer?.id ?? undefined,
        // Surface disambiguation fields for the MULTIPLE_PATIENTS_MATCHED path
        correlationId: response.data.correlationId,
        disambiguationField: response.data.disambiguationField,
      };
    } catch (error) {
      tracker.error(error);
      throw error;
    }
  },
);

/**
 * Match By Email Actor with Metadata
 */
export const matchByEmailActor: ActorWithMetadata = {
  impl: matchByEmailActorImpl,
  metadata: {
    displayName: "matchByEmail",
    description:
      "Looks up whether the patient (email) already exists in the system",
    category: "auth",
    retryable: false,
    timeout: 10000, // 10 seconds
    errorCodes: [],
    inputSchema: MatchByEmailInputSchema,
    successSchema: MatchByEmailSuccessSchema,
    errorSchema: z.object({}),
    onSuccess: (result: unknown) => {
      const r = result as MatchByEmailSuccess;
      return {
        lookupResult: { message: r.message, consumer: r.consumer },
        userId: r.consumer?.userId ?? undefined,
        consumerId: r.consumer?.id ?? undefined,
        correlationId: r.correlationId,
        disambiguationField: r.disambiguationField,
      };
    },
    onError: (_error: unknown) => {
      return {};
    },
  } satisfies ActorMetadata,
};
